import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const storages = [localStorage, sessionStorage];
  for (const storage of storages) {
    const authToken = storage.getItem("authToken");
    if (authToken?.trim()) {
      return authToken.trim();
    }
    const legacyToken = storage.getItem("idToken");
    if (legacyToken?.trim()) {
      return legacyToken.trim();
    }
  }
  return null;
};

export type LiveLogCategory = "fetch" | "mongo" | "acl" | "activity";

export function isLiveLogFetchPath(path?: string | null): boolean {
  if (!path) {
    return false;
  }
  const normalized = path.split("?")[0];
  return normalized.includes("/admin/live-logs");
}

export type LiveLogEntry = {
  id: string;
  at: string;
  category: LiveLogCategory;
  method?: string;
  path?: string;
  status?: number;
  durationMs?: number;
  userId?: string | null;
  userEmail?: string | null;
  action?: string;
  detail?: string | null;
  responseBody?: string | null;
  operation?: string;
  collection?: string;
  documentId?: string | null;
  summary?: string | null;
  ok?: boolean;
  errorMessage?: string | null;
  allowed?: boolean;
  reason?: string;
  layer?: string | null;
  policyModel?: string | null;
  action?: string;
  targetType?: string;
  targetId?: string;
  fieldKey?: string | null;
  userGroups?: string[];
  userRoles?: string[];
  systemAdmin?: boolean;
  matchedCount?: number;
  matchedUids?: string[];
  collectionUid?: string | null;
  itemOwnerId?: string | null;
  diagnostics?: {
    collectionUid?: string | null;
    grantsForAction?: number;
    grantsTotal?: number;
    nearMisses?: Array<{
      uid?: string | null;
      failedOn?: string;
      whoType?: string | null;
      who?: string | null;
      whatType?: string | null;
      what?: unknown;
    }>;
    hints?: string[];
    bypass?: {
      collectionOwnerUid?: string | null;
      tenantManagerChecked?: boolean;
      tenantManagerMatched?: boolean;
      itemCreatorBypassApplies?: boolean;
      itemCreatorMatched?: boolean;
      itemOwnerOrCreatorUid?: string | null;
    };
  } | null;
};

export type LiveLogsResponse = {
  success: boolean;
  entries: LiveLogEntry[];
  serverTime: string;
};

export async function fetchRecentLiveLogs(params?: {
  since?: string;
  limit?: number;
  categories?: LiveLogCategory[];
}): Promise<LiveLogsResponse> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }
  const search = new URLSearchParams();
  if (params?.since) {
    search.set("since", params.since);
  }
  if (params?.limit != null) {
    search.set("limit", String(params.limit));
  }
  if (params?.categories?.length) {
    search.set("categories", params.categories.join(","));
  }
  const qs = search.toString();
  const url = `${API_URL}/auth/admin/live-logs${qs ? `?${qs}` : ""}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json"
    }
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to load live logs (${response.status})`);
  }
  return response.json();
}

export type LiveLogStreamOptions = {
  categories?: LiveLogCategory[];
  onEntry: (entry: LiveLogEntry) => void;
  onError?: (error: Error) => void;
  signal?: AbortSignal;
};

export async function streamLiveLogs(options: LiveLogStreamOptions): Promise<void> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }
  const search = new URLSearchParams();
  if (options.categories?.length) {
    search.set("categories", options.categories.join(","));
  }
  const qs = search.toString();
  const url = `${API_URL}/auth/admin/live-logs/stream${qs ? `?${qs}` : ""}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream"
    },
    signal: options.signal
  });
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Live log stream failed (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const processChunk = (chunk: string) => {
    buffer += chunk;
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const lines = part.split("\n");
      for (const line of lines) {
        if (!line.startsWith("data: ")) {
          continue;
        }
        const payload = line.slice(6).trim();
        if (!payload) {
          continue;
        }
        try {
          const entry = JSON.parse(payload) as LiveLogEntry;
          options.onEntry(entry);
        } catch {
          // ignore malformed events
        }
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    processChunk(decoder.decode(value, { stream: true }));
  }
}
